import React, { useState, useRef, useEffect } from 'react';
import { useFinanceData } from '../hooks/useFinanceData';

const BudgetCategoryPicker = ({ selectedCategoryId, onCategoryChange, budgetItems, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { getCategoryColor, getCategoryEmoji, } = useFinanceData();

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const options = [
    { label: 'Select a category', value: null },
    ...budgetItems.map(item => ({ 
      label: item.title, 
      value: item.id,
      emoji: item.emoji || getCategoryEmoji(item.id),
      color: item.color || getCategoryColor(item.id)
    }))
  ];

  const selectedOption = options.find(opt => opt.value === selectedCategoryId) || options[0];

  const handleSelect = (value) => {
    onCategoryChange(value);
    setIsOpen(false);
  };

  return (
    <div className="web-budget-picker-container" ref={dropdownRef}>
      <div 
        className={`picker-button ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {selectedCategoryId && (
          <span 
            className="color-indicator"
            style={{ backgroundColor: getCategoryColor(selectedCategoryId) }}
          />
        )}
        <span className="selected-text">
          {selectedOption.emoji && (
            <span className="category-emoji">{selectedOption.emoji}</span>
          )}
          {selectedOption.label}
        </span>
        <span className="dropdown-arrow">▼</span>
      </div>

      {isOpen && (
        <div className="dropdown-menu">
          {options.map((option) => (
            <div
              key={option.value || 'default'}
              className="dropdown-item"
              onClick={() => handleSelect(option.value)}
            >
              {option.value && (
                <span
                  className="color-indicator"
                  style={{ backgroundColor: option.color || '#ddd' }}
                />
              )}
              {option.emoji && (
                <span className="category-emoji">{option.emoji}</span>
              )}
              <span className="option-text">{option.label}</span>
            </div>
          ))}
        </div>
      )}

      <style jsx>{`
        .web-budget-picker-container {
          position: relative;
          width: 100%;
        }
        
        .picker-button {
          display: flex;
          align-items: center;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          background-color: white;
          cursor: pointer;
          min-height: 42px;
          box-sizing: border-box;
          width: 100%;
        }
        
        .picker-button.disabled {
          opacity: 0.5;
          background-color: #f5f5f5;
          cursor: not-allowed;
        }
        
        .color-indicator {
          width: 12px;
          height: 12px;
          border-radius: 2px;
          margin-right: 8px;
        }
        
        .selected-text {
          flex: 1;
          font-size: 14px;
          display: flex;
          align-items: center;
        }
        
        .category-emoji {
          margin-right: 8px;
          font-size: 16px;
        }
        
        .dropdown-arrow {
          font-size: 10px;
          color: #777;
          margin-left: 8px;
        }
        
        .dropdown-menu {
          position: absolute;
          top: calc(100% + 5px);
          left: 0;
          right: 0;
          max-height: 250px;
          overflow-y: auto;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          z-index: 100;
        }
        
        .dropdown-item {
          display: flex;
          align-items: center;
          padding: 10px 12px;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .dropdown-item:hover {
          background-color: #f9f9f9;
        }
        
        .option-text {
          font-size: 14px;
        }
      `}</style>
    </div>
  );
};

export default BudgetCategoryPicker;